import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, RefreshCw, Download, Calendar,
  TrendingUp, TrendingDown, DollarSign, FileText, Users,
  AlertTriangle, CheckCircle, PieChart, BarChart3, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORT_TYPES = [
  { value: "cash_flow_summary", label: "Cash Flow Summary" },
  { value: "income_vs_expense", label: "Income vs Expense" },
  { value: "collections", label: "Collections Report" },
  { value: "client_performance", label: "Client Performance" },
  { value: "expense_breakdown", label: "Expense Breakdown" }
];

const PERIODS = [
  { value: "day", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom Range" }
];

const COLORS = {
  primary: "#047857",
  secondary: "#10b981",
  expense: "#ef4444",
  warning: "#f59e0b",
  info: "#3b82f6",
  chart: ["#047857", "#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#d1fae5", "#059669", "#0d9488"]
};

export default function DataAnalytics() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  
  // Filters
  const [reportType, setReportType] = useState("cash_flow_summary");
  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  const reportRef = useRef(null);

  useEffect(() => {
    loadBusinessProfile();
  }, []);

  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setBusinessProfile(response.data);
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    setAiSummary(null);
    try {
      const payload = {
        report_type: reportType,
        period: period,
        start_date: period === "custom" ? startDate : undefined,
        end_date: period === "custom" ? endDate : undefined
      };
      
      const response = await axios.post(`${API}/reports/analytics`, payload);
      setReport(response.data);
      toast.success("Report generated successfully");
    } catch (error) {
      console.error("Failed to generate report:", error);
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const generateAiSummary = async () => {
    if (!report) return;
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/reports/analytics/ai-summary`, report);
      setAiSummary(response.data);
    } catch (error) {
      console.error("Failed to generate AI summary:", error);
      toast.error("Failed to generate AI summary");
    } finally {
      setAiLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const exportToPDF = async () => {
    if (!reportRef.current || !report) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 10;
      
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 20);
      
      while (heightLeft > 0) {
        pdf.addPage();
        position = heightLeft - imgHeight + 10;
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 20);
      }
      
      // Footer on last page
      const lastPage = pdf.getNumberOfPages();
      pdf.setPage(lastPage);
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(`Generated by LedgerAMT on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      
      pdf.save(`analytics-report-${report.start_date}-to-${report.end_date}.pdf`);
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="data-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Data Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Generate comprehensive business reports
            </p>
          </div>
        </div>
        
        {report && (
          <Button
            variant="outline"
            onClick={exportToPDF}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Report Type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {period === "custom" && (
            <>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-input"
                />
              </div>
            </>
          )}
          
          <div className={period === "custom" ? "md:col-span-4" : ""}>
            <Button
              onClick={generateReport}
              disabled={generating || (period === "custom" && (!startDate || !endDate))}
              className="w-full md:w-auto"
              data-testid="generate-report-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-2" />
              )}
              Generate Report
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {generating ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Generating report...</p>
          </div>
        </div>
      ) : report ? (
        <div ref={reportRef} className="bg-white rounded-lg border overflow-hidden">
          {/* Report Header */}
          <div className="p-6 border-b" style={{ borderBottomColor: businessProfile?.primary_color || COLORS.primary, borderBottomWidth: "3px" }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {(businessProfile?.logo_data || businessProfile?.logo_url) && (
                  <img
                    src={businessProfile.logo_data || businessProfile.logo_url}
                    alt="Logo"
                    className="h-16 w-auto max-w-[140px] object-contain"
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold" style={{ color: businessProfile?.primary_color || COLORS.primary }}>
                    {report.business_name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {REPORT_TYPES.find(t => t.value === report.report_type)?.label || "Analytics Report"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatDate(report.start_date)} — {formatDate(report.end_date)}</p>
                <p className="text-xs text-muted-foreground">Generated {formatDate(report.generated_at)}</p>
              </div>
            </div>
          </div>

          {/* KPI Strip */}
          <div className="p-6 bg-gray-50 border-b">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <KPICard
                title="Cash In"
                value={formatCurrency(report.kpis.cash_in)}
                icon={<TrendingUp className="w-4 h-4" />}
                color="text-emerald-600"
                bgColor="bg-emerald-50"
              />
              <KPICard
                title="Cash Out"
                value={formatCurrency(report.kpis.cash_out)}
                icon={<TrendingDown className="w-4 h-4" />}
                color="text-red-500"
                bgColor="bg-red-50"
              />
              <KPICard
                title="Net Cash Flow"
                value={formatCurrency(report.kpis.net_cash_flow)}
                icon={<DollarSign className="w-4 h-4" />}
                color={report.kpis.net_cash_flow >= 0 ? "text-emerald-600" : "text-red-500"}
                bgColor={report.kpis.net_cash_flow >= 0 ? "bg-emerald-50" : "bg-red-50"}
              />
              <KPICard
                title="Invoices Issued"
                value={report.kpis.invoice_count}
                subtitle={formatCurrency(report.kpis.total_invoiced)}
                icon={<FileText className="w-4 h-4" />}
                color="text-blue-600"
                bgColor="bg-blue-50"
              />
              <KPICard
                title="Collection Rate"
                value={`${report.kpis.collection_rate}%`}
                icon={<CheckCircle className="w-4 h-4" />}
                color={report.kpis.collection_rate >= 80 ? "text-emerald-600" : "text-amber-500"}
                bgColor={report.kpis.collection_rate >= 80 ? "bg-emerald-50" : "bg-amber-50"}
              />
              <KPICard
                title="Overdue"
                value={formatCurrency(report.kpis.overdue_amount)}
                subtitle={`${report.kpis.overdue_count} invoices`}
                icon={<AlertTriangle className="w-4 h-4" />}
                color="text-red-500"
                bgColor="bg-red-50"
              />
            </div>
          </div>

          {/* Charts Section */}
          <div className="p-6 border-b">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Cash Flow Trend */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  Cash Flow Trend
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={report.charts.cash_flow_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), ""]}
                        labelStyle={{ fontWeight: 600 }}
                        contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="income" name="Income" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="expense" name="Expense" stroke={COLORS.expense} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Expense by Category */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  Expenses by Category
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.charts.expense_by_category} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} stroke="#9ca3af" width={80} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), "Amount"]}
                        contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                      <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                        {report.charts.expense_by_category.map((_, index) => (
                          <Cell key={index} fill={COLORS.chart[index % COLORS.chart.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue by Client Chart */}
          {report.charts.revenue_by_client?.length > 0 && (
            <div className="p-6 border-b">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                Revenue by Client
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.charts.revenue_by_client}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="client" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                    <Tooltip
                      formatter={(value) => [formatCurrency(value), "Revenue"]}
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                    />
                    <Bar dataKey="revenue" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* AI Summary Section */}
          <div className="p-6 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-500" />
                AI Insights
              </h3>
              {!aiSummary && (
                <Button variant="outline" size="sm" onClick={generateAiSummary} disabled={aiLoading}>
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Insights"}
                </Button>
              )}
            </div>
            
            {aiLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Analyzing your data...</span>
              </div>
            ) : aiSummary ? (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Key Insights</p>
                  <ul className="space-y-2">
                    {aiSummary.insights?.map((insight, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-emerald-500 mt-1">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Suggested Actions</p>
                  <ul className="space-y-2">
                    {aiSummary.actions?.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-blue-500 mt-1">→</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Click "Generate Insights" for AI-powered analysis of your data.
              </p>
            )}
          </div>

          {/* Tables Section */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Overdue Invoices Table */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Overdue Invoices
                </h3>
                {report.tables.overdue_invoices?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium text-muted-foreground">Invoice</th>
                          <th className="text-left py-2 font-medium text-muted-foreground">Client</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Days</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.tables.overdue_invoices.map((inv, i) => (
                          <tr key={i} className="border-b border-dashed">
                            <td className="py-2 font-mono text-xs">{inv.invoice_number}</td>
                            <td className="py-2 truncate max-w-[120px]">{inv.client_name}</td>
                            <td className="py-2 text-right text-red-500 font-medium">{inv.days_overdue}d</td>
                            <td className="py-2 text-right font-medium">{formatCurrency(inv.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No overdue invoices</p>
                )}
              </div>

              {/* Top Clients Table */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  Top Clients by Outstanding
                </h3>
                {report.tables.top_clients?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium text-muted-foreground">Client</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Invoiced</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Paid</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.tables.top_clients.map((client, i) => (
                          <tr key={i} className="border-b border-dashed">
                            <td className="py-2 truncate max-w-[120px]">{client.client_name}</td>
                            <td className="py-2 text-right">{formatCurrency(client.invoiced)}</td>
                            <td className="py-2 text-right text-emerald-600">{formatCurrency(client.paid)}</td>
                            <td className="py-2 text-right font-medium text-amber-600">{formatCurrency(client.outstanding)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No client data</p>
                )}
              </div>
            </div>

            {/* Expense Categories Table */}
            {report.tables.expense_categories?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-emerald-500" />
                  Expense Breakdown
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {report.tables.expense_categories.map((cat, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-muted-foreground truncate">{cat.category}</p>
                      <p className="font-semibold">{formatCurrency(cat.amount)}</p>
                      <p className="text-xs text-muted-foreground">{cat.percentage}% of total</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t text-center">
            <p className="text-xs text-muted-foreground">
              Generated by LedgerAMT • {formatDate(report.start_date)} to {formatDate(report.end_date)}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-12 text-center">
          <BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Report Generated</h3>
          <p className="text-muted-foreground mb-6">
            Select a report type and period, then click "Generate Report" to view your analytics.
          </p>
          <Button onClick={generateReport} disabled={generating}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>
      )}
    </div>
  );
}

// KPI Card Component
function KPICard({ title, value, subtitle, icon, color, bgColor }) {
  return (
    <div className={`p-4 rounded-lg ${bgColor}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={color}>{icon}</span>
        <span className="text-xs text-muted-foreground font-medium">{title}</span>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
